import { MenuItem, styled, type MenuItemProps, type MenuListProps } from '@mui/material';
import type { KeyboardEvent, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Tooltip } from '~/components/Tooltip';

/**
 * Spread into a menu's `MenuListProps` so the arrow keys also land on disabled items, letting
 * keyboard users find them and hear why they are disabled. MUI still runs a disabled item's
 * `onClick` on Space (Enter is already blocked), so Space is cancelled before it gets there.
 */
export const focusableDisabledItems: Partial<MenuListProps> = {
  disabledItemsFocusable: true,
  onKeyUpCapture: (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key !== ' ') return;
    if (!(event.target as Element).closest('[aria-disabled="true"]')) return;
    event.preventDefault();
  },
};

// A disabled item takes the pointer for its tooltip, without the hover that suggests it works.
const HoverableMenuItem = styled(MenuItem)({
  '&.Mui-disabled': { pointerEvents: 'auto' },
  '&.Mui-disabled:hover': { backgroundColor: 'transparent' },
});

export interface TooltipMenuItemProps extends MenuItemProps {
  /** Shown on hover and keyboard focus, typically why the item is disabled. */
  tooltip?: ReactNode;
}

/**
 * A menu item with a tooltip on the item itself. Menus move focus only between their direct
 * children, so wrapping an item for its tooltip would hide it from the arrow keys.
 */
export const TooltipMenuItem = forwardRef<HTMLLIElement, TooltipMenuItemProps>(
  function TooltipMenuItem({ disabled, onClick, tooltip, ...props }, ref) {
    return (
      // An empty title never opens, so the item can keep one tree whether or not it has a tooltip.
      <Tooltip placement="left" title={tooltip ?? ''}>
        <HoverableMenuItem
          {...props}
          ref={ref}
          disabled={disabled}
          // Taking the pointer means taking clicks too, so a disabled item drops them here.
          onClick={disabled ? undefined : onClick}
        />
      </Tooltip>
    );
  },
);
